import React, { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_PHONE_PREFIX,
  PHONE_PREFIX_OPTIONS,
  formatPhoneNumber,
  parsePhoneNumber,
  phoneDigitsOnly,
} from '../../utils/phoneFormatting';

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  placeholder?: string;
}

export function PhoneNumberInput({
  value,
  onChange,
  label,
  error,
  placeholder = '896015979',
}: PhoneNumberInputProps) {
  const [prefix, setPrefix] = useState(DEFAULT_PHONE_PREFIX);
  const [localNumber, setLocalNumber] = useState('');

  useEffect(() => {
    const parsed = parsePhoneNumber(value);
    setPrefix(parsed.prefix);
    setLocalNumber(parsed.localNumber);
  }, [value]);

  const emitChange = (nextPrefix: string, nextLocal: string) => {
    onChange(formatPhoneNumber(nextPrefix, nextLocal));
  };

  const handlePrefixChange = (nextPrefix: string) => {
    setPrefix(nextPrefix);
    emitChange(nextPrefix, localNumber);
  };

  const prefixOptions = useMemo(() => {
    if (PHONE_PREFIX_OPTIONS.some((option) => option.code === prefix)) {
      return PHONE_PREFIX_OPTIONS;
    }
    return [{ code: prefix, label: `${prefix} (custom)` }, ...PHONE_PREFIX_OPTIONS];
  }, [prefix]);

  const handleLocalChange = (raw: string) => {
    if (raw.includes('+') || raw.startsWith('00')) {
      const parsed = parsePhoneNumber(raw);
      setPrefix(parsed.prefix);
      setLocalNumber(parsed.localNumber);
      emitChange(parsed.prefix, parsed.localNumber);
      return;
    }

    const nextLocal = phoneDigitsOnly(raw);
    setLocalNumber(nextLocal);
    emitChange(prefix, nextLocal);
  };

  return (
    <div>
      {label && <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>}
      <div className="flex gap-2">
        <select
          value={prefix}
          onChange={(e) => handlePrefixChange(e.target.value)}
          className={`w-52 shrink-0 px-2 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-300 bg-red-50' : 'border-slate-200'
          }`}
          aria-label="Country calling code"
        >
          {prefixOptions.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
        <input
          type="tel"
          value={localNumber}
          onChange={(e) => handleLocalChange(e.target.value)}
          placeholder={placeholder}
          className={`flex-1 min-w-0 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-300 bg-red-50' : 'border-slate-200'
          }`}
        />
      </div>
      <p className="mt-1 text-xs text-slate-500">Saved as e.g. +353 896015979</p>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
